import { useState, useEffect, useRef } from "react";
import { Plus, Trash2 } from "lucide-react";

import {
  Field,
  TextInput,
  TextArea,
  CollapsibleSection,
  ReorderButtons,
} from "../../../components/FormField";

import { moveItem } from "./shared";

// ─── Defined OUTSIDE to prevent remount on every render ───────────────────────
function StringList({ items, onChange, addLabel, placeholder }) {
  const updateItem = (i, val) =>
    onChange(items.map((x, idx) => (idx === i ? val : x)));
  const removeItem = (i) => onChange(items.filter((_, idx) => idx !== i));
  const addItem = () => onChange([...items, ""]);

  return (
    <>
      {items.map((item, i) => (
        <div key={i} className="flex gap-2 items-center mb-2">
          <div className="flex-1">
            <TextInput
              value={item}
              placeholder={placeholder || `Item ${i + 1}`}
              onChange={(e) => updateItem(i, e.target.value)}
            />
          </div>
          <ReorderButtons
            index={i}
            total={items.length}
            onMove={(f, t) => onChange(moveItem(items, f, t))}
          />
          {items.length > 1 && (
            <button onClick={() => removeItem(i)}>
              <Trash2 size={14} />
            </button>
          )}
        </div>
      ))}
      <button
        onClick={addItem}
        className="flex items-center gap-1 text-xs mt-1"
      >
        <Plus size={13} /> {addLabel}
      </button>
    </>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export function ContactSections({ setSectionsData, initialData }) {
  /* ---------------- HERO ---------------- */
  const [heroActive, setHeroActive] = useState(true);
  const [heroBadgeText, setHeroBadgeText] = useState("Contact Us");
  const [heroHeading, setHeroHeading] = useState("Ready to Transform Your");
  const [heroHeadingEmphasis, setHeroHeadingEmphasis] = useState("Business?");
  const [heroDescription, setHeroDescription] = useState("");

  /* ---------------- FORM SECTION ---------------- */
  const [formActive, setFormActive] = useState(true);
  const [formCardHeading, setFormCardHeading] = useState(
    "Book a Free Consultation",
  );
  const [formButtonText, setFormButtonText] = useState(
    "Book My Free Consultation",
  );
  const [formSubmittingText, setFormSubmittingText] = useState("Sending...");
  const [formDisclaimer, setFormDisclaimer] = useState(
    "All discovery calls are obligation-free and tailored to your needs.",
  );
  const [industries, setIndustries] = useState([
    "Food & Beverage",
    "Manufacturing",
    "Wholesale & Distribution",
    "Healthcare & Pharma",
    "Construction & Engineering",
    "Retail & Omnichannel",
    "Agriculture & Agribusiness",
    "Professional Services",
    "Not-for-Profit (NDIS & Care)",
    "Other",
  ]);
  const [interests, setInterests] = useState([
    "ERP Implementation (SAP Business One)",
    "SAP Cloud ERP",
    "Salesforce CRM",
    "eCommerce Solutions",
    "Analytics & BI",
    "Integration & Automation",
    "Support & Managed Services",
    "Strategic Advisory",
    "Other",
  ]);

  /* ---------------- CONTACT INFO ---------------- */
  const [infoActive, setInfoActive] = useState(true);
  const [infoCardHeading, setInfoCardHeading] = useState("Get in Touch");
  const [infoPhone, setInfoPhone] = useState("+61 3 7075 3900");
  const [infoPhoneSub, setInfoPhoneSub] = useState(
    "Call us during business hours",
  );
  const [infoPhoneHref, setInfoPhoneHref] = useState("tel:0370753900");
  const [infoEmail, setInfoEmail] = useState("info@thynkprocess.com.au");
  const [infoEmailSub, setInfoEmailSub] = useState(
    "We respond within 24 hours",
  );
  const [infoAddress, setInfoAddress] = useState(
    "39/72 York Street, South Melbourne Victoria 3205",
  );
  const [infoAddressSub, setInfoAddressSub] = useState("Australia");
  const [infoAddressHref, setInfoAddressHref] = useState(
    "https://maps.app.goo.gl/pg5ETTnxr1SujJiS6",
  );
  const [infoHoursLabel, setInfoHoursLabel] = useState("Business Hours");
  const [infoHoursValue, setInfoHoursValue] = useState(
    "Mon - Fri: 9:00 AM - 5:30 PM AEST",
  );

  /* ---------------- WHY CHOOSE US ---------------- */
  const [whyActive, setWhyActive] = useState(true);
  const [whyHeading, setWhyHeading] = useState("Why Choose Us?");
  const [whyItems, setWhyItems] = useState([
    "SAP Silver Partner",
    "150+ successful projects",
    "60+ years combined experience",
    "Australian-based support",
  ]);

  /* ---------------- POPULATE FROM API ---------------- */
  useEffect(() => {
    if (!initialData) return;
    const d = initialData;
    if (d.hero) {
      setHeroBadgeText(d.hero.badge_text || "");
      setHeroHeading(d.hero.heading || "");
      setHeroHeadingEmphasis(d.hero.heading_emphasis || "");
      setHeroDescription(d.hero.description || "");
    }
    if (d.form_section) {
      setFormCardHeading(d.form_section.card_heading || "");
      setFormButtonText(d.form_section.button_text || "");
      setFormSubmittingText(d.form_section.submitting_text || "");
      setFormDisclaimer(d.form_section.disclaimer || "");
      if (d.form_section.industries?.length)
        setIndustries(d.form_section.industries);
      if (d.form_section.interests?.length)
        setInterests(d.form_section.interests);
    }
    if (d.contact_info) {
      setInfoCardHeading(d.contact_info.card_heading || "");
      setInfoPhone(d.contact_info.phone || "");
      setInfoPhoneSub(d.contact_info.phone_sub || "");
      setInfoPhoneHref(d.contact_info.phone_href || "");
      setInfoEmail(d.contact_info.email || "");
      setInfoEmailSub(d.contact_info.email_sub || "");
      setInfoAddress(d.contact_info.address || "");
      setInfoAddressSub(d.contact_info.address_sub || "");
      setInfoAddressHref(d.contact_info.address_href || "");
      setInfoHoursLabel(d.contact_info.hours_label || "");
      setInfoHoursValue(d.contact_info.hours_value || "");
    }
    if (d.why_choose) {
      setWhyHeading(d.why_choose.heading || "");
      if (d.why_choose.items?.length) setWhyItems(d.why_choose.items);
    }
  }, [initialData]);

  /* ---------------- SYNC TO PARENT (debounced) ---------------- */
  const ref = useRef({});
  ref.current = {
    heroBadgeText,
    heroHeading,
    heroHeadingEmphasis,
    heroDescription,
    formCardHeading,
    formButtonText,
    formSubmittingText,
    formDisclaimer,
    industries,
    interests,
    infoCardHeading,
    infoPhone,
    infoPhoneSub,
    infoPhoneHref,
    infoEmail,
    infoEmailSub,
    infoAddress,
    infoAddressSub,
    infoAddressHref,
    infoHoursLabel,
    infoHoursValue,
    whyHeading,
    whyItems,
  };

  useEffect(() => {
    const t = setTimeout(() => {
      const s = ref.current;
      setSectionsData?.({
        hero: {
          badge_text: s.heroBadgeText,
          heading: s.heroHeading,
          heading_emphasis: s.heroHeadingEmphasis,
          description: s.heroDescription,
        },
        form_section: {
          card_heading: s.formCardHeading,
          button_text: s.formButtonText,
          submitting_text: s.formSubmittingText,
          disclaimer: s.formDisclaimer,
          industries: s.industries,
          interests: s.interests,
        },
        contact_info: {
          card_heading: s.infoCardHeading,
          phone: s.infoPhone,
          phone_sub: s.infoPhoneSub,
          phone_href: s.infoPhoneHref,
          email: s.infoEmail,
          email_sub: s.infoEmailSub,
          address: s.infoAddress,
          address_sub: s.infoAddressSub,
          address_href: s.infoAddressHref,
          hours_label: s.infoHoursLabel,
          hours_value: s.infoHoursValue,
        },
        why_choose: { heading: s.whyHeading, items: s.whyItems },
      });
    }, 400);
    return () => clearTimeout(t);
  }, [
    heroBadgeText,
    heroHeading,
    heroHeadingEmphasis,
    heroDescription,
    formCardHeading,
    formButtonText,
    formSubmittingText,
    formDisclaimer,
    industries,
    interests,
    infoCardHeading,
    infoPhone,
    infoPhoneSub,
    infoPhoneHref,
    infoEmail,
    infoEmailSub,
    infoAddress,
    infoAddressSub,
    infoAddressHref,
    infoHoursLabel,
    infoHoursValue,
    whyHeading,
    whyItems,
  ]);

  /* ---------------- RENDER ---------------- */
  return (
    <>
      {/* HERO */}
      <CollapsibleSection
        title="Hero Section"
        active={heroActive}
        onToggleActive={setHeroActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={heroBadgeText}
            onChange={(e) => setHeroBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={heroHeading}
            onChange={(e) => setHeroHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={heroHeadingEmphasis}
            onChange={(e) => setHeroHeadingEmphasis(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={heroDescription}
            onChange={(e) => setHeroDescription(e.target.value)}
          />
        </Field>
      </CollapsibleSection>

      {/* FORM SECTION */}
      <CollapsibleSection
        title="Contact Form"
        active={formActive}
        onToggleActive={setFormActive}
      >
        <Field label="Form Card Heading">
          <TextInput
            value={formCardHeading}
            onChange={(e) => setFormCardHeading(e.target.value)}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Submit Button Text">
            <TextInput
              value={formButtonText}
              onChange={(e) => setFormButtonText(e.target.value)}
            />
          </Field>
          <Field label="Submitting Text">
            <TextInput
              value={formSubmittingText}
              placeholder="Sending..."
              onChange={(e) => setFormSubmittingText(e.target.value)}
            />
          </Field>
        </div>
        <Field label="Disclaimer Text">
          <TextArea
            value={formDisclaimer}
            onChange={(e) => setFormDisclaimer(e.target.value)}
          />
        </Field>

        <p className="text-xs font-semibold mt-5 mb-2">
          Industry Dropdown Options
        </p>
        <StringList
          items={industries}
          onChange={setIndustries}
          addLabel="Add Industry"
          placeholder="e.g. Manufacturing"
        />

        <p className="text-xs font-semibold mt-5 mb-2">
          Area of Interest Dropdown Options
        </p>
        <StringList
          items={interests}
          onChange={setInterests}
          addLabel="Add Interest"
          placeholder="e.g. SAP Business One"
        />
      </CollapsibleSection>

      {/* CONTACT INFO */}
      <CollapsibleSection
        title="Contact Info Sidebar"
        active={infoActive}
        onToggleActive={setInfoActive}
      >
        <Field label="Card Heading">
          <TextInput
            value={infoCardHeading}
            onChange={(e) => setInfoCardHeading(e.target.value)}
          />
        </Field>
        <p className="text-xs font-semibold text-muted-foreground mt-4 mb-2">
          Phone
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Phone Number">
            <TextInput
              value={infoPhone}
              placeholder="+61 3 7075 3900"
              onChange={(e) => setInfoPhone(e.target.value)}
            />
          </Field>
          <Field label="Phone Sub Label">
            <TextInput
              value={infoPhoneSub}
              onChange={(e) => setInfoPhoneSub(e.target.value)}
            />
          </Field>
        </div>
        <Field label="Phone href (tel:...)">
          <TextInput
            value={infoPhoneHref}
            placeholder="tel:0370753900"
            onChange={(e) => setInfoPhoneHref(e.target.value)}
          />
        </Field>
        <p className="text-xs font-semibold text-muted-foreground mt-4 mb-2">
          Email
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Email Address">
            <TextInput
              value={infoEmail}
              onChange={(e) => setInfoEmail(e.target.value)}
            />
          </Field>
          <Field label="Email Sub Label">
            <TextInput
              value={infoEmailSub}
              onChange={(e) => setInfoEmailSub(e.target.value)}
            />
          </Field>
        </div>
        <p className="text-xs font-semibold text-muted-foreground mt-4 mb-2">
          Address
        </p>
        <Field label="Address">
          <TextInput
            value={infoAddress}
            onChange={(e) => setInfoAddress(e.target.value)}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Address Sub Label">
            <TextInput
              value={infoAddressSub}
              onChange={(e) => setInfoAddressSub(e.target.value)}
            />
          </Field>
          <Field label="Google Maps URL">
            <TextInput
              value={infoAddressHref}
              placeholder="https://maps.app.goo.gl/..."
              onChange={(e) => setInfoAddressHref(e.target.value)}
            />
          </Field>
        </div>
        <p className="text-xs font-semibold text-muted-foreground mt-4 mb-2">
          Business Hours
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Hours Label">
            <TextInput
              value={infoHoursLabel}
              onChange={(e) => setInfoHoursLabel(e.target.value)}
            />
          </Field>
          <Field label="Hours Value">
            <TextInput
              value={infoHoursValue}
              placeholder="Mon - Fri: 9:00 AM - 5:30 PM AEST"
              onChange={(e) => setInfoHoursValue(e.target.value)}
            />
          </Field>
        </div>
      </CollapsibleSection>

      {/* WHY CHOOSE US */}
      <CollapsibleSection
        title="Why Choose Us"
        active={whyActive}
        onToggleActive={setWhyActive}
      >
        <Field label="Heading">
          <TextInput
            value={whyHeading}
            onChange={(e) => setWhyHeading(e.target.value)}
          />
        </Field>
        <p className="text-xs font-semibold mt-3 mb-2">List Items</p>
        <StringList
          items={whyItems}
          onChange={setWhyItems}
          addLabel="Add Item"
          placeholder="e.g. SAP Silver Partner"
        />
      </CollapsibleSection>
    </>
  );
}
